import React, { useEffect, useState } from 'react';
import { TKeyMetricsItem } from '../../kpiTypes';
import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import { formatValueAsString } from '../../../../utils/helpers';
import { ChartContainer } from '../../../../shared/components/charts/ChartContainer';

export interface ILoadedConditionProps {
	chartKey: number;
	keyMetricsData: TKeyMetricsItem;
}

const LoadedConditionChart: React.FC<ILoadedConditionProps> = ({ ...props }) => {
	const formatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };

	if (!props.keyMetricsData || props.keyMetricsData.voyagesCount === 0) {
		return;
	}

	const [chartData, setChartData] = useState<{ voyagesCount: number; ballastPercentage: number; ladenPercentage: number }[]>([]);

	// Update chart data when props change
	useEffect(() => {
		const ballastCount = props.keyMetricsData.ballastCount;
		const ladenCount = props.keyMetricsData.ladenCount;
		const total = ballastCount + ladenCount;
		const ballastPercentage = (ballastCount / total) * 100;
		const ladenPercentage = (ladenCount / total) * 100;

		const data = [
			{
				voyagesCount: props.keyMetricsData.voyagesCount,
				ballastPercentage: ballastPercentage,
				ladenPercentage: ladenPercentage,
			},
		];

		setChartData(data);
		setOptions((prevOptions) => ({
			...prevOptions,
			data: data,
		}));
	}, [props.keyMetricsData]);

	const [options, setOptions] = useState<AgChartOptions>({
		data: chartData,
		series: [
			{
				type: 'bar',
				xKey: 'voyagesCount',
				yKey: 'ballastPercentage',
				yName: 'Ballast',
				stacked: true,
				normalizedTo: 100,
				fill: '#f0d035',
				tooltip: {
					renderer(params) {
						return {
							title: `Ballast`,
							heading: formatValueAsString(params.datum.ballastPercentage, formatOptions, '', '', '%'),
							data: undefined,
						};
					},
				},
			},
			{
				type: 'bar',
				xKey: 'voyagesCount',
				yKey: 'ladenPercentage',
				yName: 'Laden',
				stacked: true,
				normalizedTo: 100,
				fill: '#183d57',
				tooltip: {
					renderer(params) {
						return {
							data: undefined,
							title: `Laden`,
							heading: formatValueAsString(params.datum.ladenPercentage, formatOptions, '', '', '%'),
						};
					},
				},
			},
		],
		axes: [
			{
				type: 'category',
				label: { enabled: false },
			},
			{
				type: 'number',
				position: 'left',
				label: {
					avoidCollisions: true,
					formatter: ({ value }: { value: number }) => {
						return formatValueAsString(value, formatOptions, '', '', '%');
					},
				},
			},
		],
		legend: {
			position: 'bottom',
		},
	});

	const downloadableData = chartData.map((item) => ({
		ballastPercentage: item.ballastPercentage,
		ladenPercentage: item.ladenPercentage,
	}));

	return (
		<ChartContainer data={downloadableData} filename="loaded_condition_chart" buttonPosition="top-right">
			<AgCharts key={props.chartKey} options={options} className="w-full h-full" />
		</ChartContainer>
	);
};

export default LoadedConditionChart;
